import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Plus, Trash2, RotateCcw, Save, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const AdminCalendar = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState("");

  const token = () => localStorage.getItem("token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const load = async (y) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/work-calendar/${y}`);
      setCalendar(r.data);
    } catch (e) {
      toast.error("Erro ao carregar calendário");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const addHoliday = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newHoliday)) {
      toast.error("Formato inválido. Usa YYYY-MM-DD");
      return;
    }
    if (!newHoliday.startsWith(`${year}-`)) {
      toast.error(`Data deve ser do ano ${year}`);
      return;
    }
    if (calendar.holidays.includes(newHoliday)) {
      toast.info("Data já consta");
      return;
    }
    setCalendar({ ...calendar, holidays: [...calendar.holidays, newHoliday].sort() });
    setNewHoliday("");
  };

  const removeHoliday = (h) => {
    setCalendar({ ...calendar, holidays: calendar.holidays.filter(x => x !== h) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/work-calendar/${year}`,
        { year, country: "PT", holidays: calendar.holidays, description: calendar.description || null },
        { headers: authHeaders() }
      );
      setCalendar(r.data);
      toast.success(`Calendário ${year} guardado. ${r.data.total_working_days} dias úteis anuais.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Repor feriados default PT nacional para ${year}?`)) return;
    try {
      const r = await axios.post(`${API}/work-calendar/${year}/reset`, {}, { headers: authHeaders() });
      setCalendar(r.data);
      toast.success(`Calendário ${year} reposto para default`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao repor");
    }
  };

  const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6" /> Calendário Laboral PT
          </h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Ano</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setYear(year - 1)}>−</Button>
                <Input
                  type="number"
                  className="w-24 text-center"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value) || year)}
                  data-testid="year-input"
                />
                <Button variant="outline" size="sm" onClick={() => setYear(year + 1)}>+</Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !calendar ? (
              <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4 text-sm">
                  <Badge variant={calendar.source === "db" ? "default" : "secondary"}>
                    {calendar.source === "db" ? "Config personalizada" : "Default hardcoded"}
                  </Badge>
                  <span className="text-slate-600">
                    Total: <b>{calendar.total_working_days}</b> dias úteis anuais ·
                    Média: <b>{calendar.average_per_month}</b>/mês
                  </span>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2 mb-6">
                  {calendar.working_days_per_month.map((d, i) => (
                    <div key={i} className="text-center p-2 bg-brand-50 rounded">
                      <div className="text-[10px] text-slate-600">{MESES[i]}</div>
                      <div className="font-bold text-brand-700" data-testid={`days-${i + 1}`}>{d}d</div>
                    </div>
                  ))}
                </div>

                <Label className="font-semibold mb-2 block">Feriados ({calendar.holidays.length})</Label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {calendar.holidays.map((h) => (
                    <Badge key={h} variant="outline" className="pr-1">
                      {h}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 ml-1"
                        onClick={() => removeHoliday(h)}
                        data-testid={`remove-${h}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2 mb-4">
                  <Input
                    type="date"
                    value={newHoliday}
                    onChange={(e) => setNewHoliday(e.target.value)}
                    className="max-w-xs"
                    data-testid="new-holiday-input"
                  />
                  <Button onClick={addHoliday} data-testid="add-holiday-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={save} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Guardar
                  </Button>
                  <Button variant="outline" onClick={reset} data-testid="reset-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> Repor Default PT
                  </Button>
                </div>

                <p className="text-xs text-slate-500 mt-4">
                  Nota: estes dias úteis são usados na Simulação Anual para calcular o Sub. Alimentação
                  mês a mês (valor diário × dias úteis do mês). Feriados nacionais default incluem
                  Carnaval (facultativo mas tradicionalmente observado).
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCalendar;
